import { Spinner } from 'components/core';
import { findModelForEditing, submitServerChange } from 'components/dataCreationForms/common';
import {
  makeHandleFormValidation,
  validate,
} from 'components/dataCreationForms/validation';
import { useCompanies } from 'store/company';
import { getContractsStoreState, useContracts } from 'store/contract';
import {
  contractEditOnViewToContractEditModel,
  contractModelToContractOnView,
} from 'shared/domain/contract/mapping';
import { contractClient } from 'shared/domain/contract/contractClient';
import { ContractEditOnView } from 'shared/domain/contract/types/view';
import { useProjectId } from 'store/project';
import { ErrorPresentation } from 'helpers/validators';
import { useCancelConfirmation } from 'presentation/dialogForms/dialogFormsHooks';
import {
  ComponentType,
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  useAppUrlParams,
  UrlParamName,
} from 'hooks/navigation/useAppUrlParams';
import { EditContractContext } from '../types';
import { getEditContractValidationSchema } from '../validation';
import { useAppNavigation } from 'contexts/navigation';

const formName = 'contract';
const SUBMIT_EVENT_TYPE = 'submit-contract';
const WithEditContractContext = createContext<
  EditContractContext | undefined
>(undefined);

function WithEditContract({
  children,
  onSuccessCallback,
}: {
  children: ReactElement;
  onSuccessCallback?: (_id: string, label: string) => void;
}): ReactElement {
  const { goToContractDetails } = useAppNavigation();
  const intl = useIntl();
  const projectId = useProjectId();

  const [isPosting, setIsPosting] = useState<boolean>(false);
  const releaseSubmitEvent = useCallback(() => {
    window.dispatchEvent(new CustomEvent(SUBMIT_EVENT_TYPE));
  }, []);

  const {
    items: contracts,
    loading: loadingContracts,
  } = useContracts();
  const {
    items: companies,
    loading: loadingCompanies,
  } = useCompanies();
  const currentContractId = useAppUrlParams(UrlParamName.CONTRACT_ID);

  const [existingValues, setExistingValues] = useState<
    ContractEditOnView | undefined
  >(undefined);

  useEffect(() => {
    const existingContractModel = findModelForEditing(
      contracts,
      currentContractId
    );
    let contractOnView;
    if (existingContractModel) {
      contractOnView = contractModelToContractOnView(
        existingContractModel,
        companies
      );
      setExistingValues(contractOnView);
    }
  }, [contracts, intl, currentContractId, companies]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const validationSchema = useMemo(() => {
    return getEditContractValidationSchema(contracts, currentContractId);
  }, [contracts, currentContractId]);

  const validateCreateContract = useCallback(
    (
      key: keyof ContractEditOnView,
      value: ContractEditOnView[keyof ContractEditOnView]
    ): ErrorPresentation => {
      return validate(key, value, validationSchema);
    },
    [validationSchema]
  );

  const handleFormValidation = useMemo(() => {
    return makeHandleFormValidation(validationSchema);
  }, [validationSchema]);

  const openDialog = useCallback(() => {
    setIsDialogOpen(true);
  }, []);

  const onSuccess = useCallback(
    (_id, label) => onSuccessCallback?.(_id, label),
    [onSuccessCallback]
  );

  const confirmCancel = useCancelConfirmation(formName);
  const openConfirmationDialog = useCallback(async () => {
    const { cancelConfirmed } = await confirmCancel();
    if (!cancelConfirmed) {
      return;
    }
    setIsDialogOpen(false);
  }, [confirmCancel]);

  const submitForm = useCallback(
    async (values: ContractEditOnView) => {
      const contractEditModel = contractEditOnViewToContractEditModel(values, projectId);
      const { saved } = await submitServerChange({
        change: () => contractClient.edit(contractEditModel),
        setOpen: setIsDialogOpen,
        setIsPosting,
        action: 'updating a contract',
      });
      if (!saved) {
        return;
      }

      // The details view reads the contract from the store.
      await getContractsStoreState().actions.reload();
      goToContractDetails(contractEditModel._id);
    },
    [goToContractDetails, projectId]
  );

  const ctx: EditContractContext = useMemo(() => {
    return {
      open: isDialogOpen,
      openDialog,
      closeDialog: openConfirmationDialog,
      submitForm,
      isPosting,
      existingValues,
      SUBMIT_EVENT_TYPE,
      releaseSubmitEvent,
      validate: validateCreateContract,
      handleFormValidation,
      onSuccess,
      formName,
    };
  }, [
    openConfirmationDialog,
    validateCreateContract,
    handleFormValidation,
    existingValues,
    isPosting,
    isDialogOpen,
    openDialog,
    releaseSubmitEvent,
    submitForm,
    onSuccess,
  ]);
  if (loadingContracts || loadingCompanies) {
    return (
      <Spinner
        reason={`ContractWizard loadingContracts: ${loadingContracts} || loadingSites: ${loadingCompanies} || existingValues: ${existingValues}`}
      />
    );
  }
  return (
    <WithEditContractContext.Provider value={ctx}>
      {children}
    </WithEditContractContext.Provider>
  );
}

function useEditContract(): EditContractContext {
  const context = useContext(WithEditContractContext);
  if (context === undefined) {
    throw new Error(
      'useEditContract must be used within an EditContractContext'
    );
  }
  return context;
}

const withEditContract =
  (Component: ComponentType<any>) =>
  ({ ...props }): ReactElement => (
    <WithEditContract>
      <Component {...props} />
    </WithEditContract>
  );

export { WithEditContract, useEditContract, withEditContract };
